import React, { useState } from 'react';
import { connectElectronApp } from '../../utils/electronBridge';

const isLinux = navigator.userAgent.toLowerCase().includes('linux');
const FILE_NAME = isLinux ? 'freeli-call.deb' : 'freeli-call.exe';
const DOWNLOAD_URL = isLinux
    ? 'https://wfss001.freeli.io/common/freeli-call.deb'
    : 'https://wfss001.freeli.io/common/freeli-call.exe';

const STEPS = [
    { label: 'Setup & install' },
    { label: 'Connect' },
];

function StepDot({ index, current }) {
    const done = index < current;
    const active = index === current;
    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
                width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600,
                background: done || active ? '#318fff' : '#e4e8ee',
                color: done || active ? '#fff' : '#636e82',
                border: active ? '2px solid #1a6fd4' : 'none',
                flexShrink: 0,
            }}>
                {done ? '✓' : index + 1}
            </div>
            <span style={{
                marginLeft: 6, fontSize: 12, fontWeight: active ? 600 : 400,
                color: active ? '#0b1f47' : done ? '#318fff' : '#636e82',
                whiteSpace: 'nowrap',
            }}>{STEPS[index].label}</span>
        </div>
    );
}

function Connector({ done }) {
    return (
        <div style={{
            flex: 1, height: 2, margin: '0 8px',
            background: done ? '#318fff' : '#e4e8ee',
            minWidth: 20,
        }} />
    );
}

function FloatingDownload({ fileName, progress }) {
    return (
        <div style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 10000,
            background: '#0b1f47',
            borderRadius: 14,
            padding: '14px 18px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.38)',
            minWidth: 260, maxWidth: 320,
            color: '#fff',
            userSelect: 'none',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'rgba(49,143,255,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, flexShrink: 0,
                }}>⬇</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.55, marginBottom: 2, letterSpacing: 0.3 }}>
                        DOWNLOADING
                    </div>
                    <div style={{
                        fontSize: 13, fontWeight: 600,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {fileName}
                    </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#318fff', flexShrink: 0 }}>
                    {progress}%
                </div>
            </div>
            <div style={{
                height: 5, background: 'rgba(255,255,255,0.12)',
                borderRadius: 3, overflow: 'hidden',
            }}>
                <div style={{
                    height: '100%',
                    width: progress + '%',
                    background: 'linear-gradient(90deg, #318fff, #1a6fd4)',
                    borderRadius: 3,
                    transition: 'width 0.25s ease',
                }} />
            </div>
            <div style={{ fontSize: 11, opacity: 0.45, marginTop: 8, textAlign: 'center' }}>
                Do not close the browser tab
            </div>
        </div>
    );
}

export default function DesktopAppWizard({ onClose, userMe }) {
    const [step, setStep] = useState(0);
    const [dlState, setDlState] = useState('idle');
    const [progress, setProgress] = useState(0);
    const [installed, setInstalled] = useState(false);
    // 'idle' | 'connecting' | 'connected' | 'not-found'
    const [autoState, setAutoState] = useState('not-found');

    async function handleDownload() {
        if (dlState === 'downloading') return;
        setDlState('downloading');
        setProgress(0);
        try {
            const response = await fetch(DOWNLOAD_URL);
            if (!response.ok) throw new Error('HTTP ' + response.status);
            const contentLength = +response.headers.get('content-length') || 0;
            const reader = response.body.getReader();
            let received = 0;
            const chunks = [];
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
                if (contentLength > 0) {
                    setProgress(Math.round((received / contentLength) * 100));
                }
            }
            const blob = new Blob(chunks);
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = FILE_NAME;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
            setProgress(100);
            setDlState('downloaded');
        } catch (_err) {
            // CORS / network — fall back to browser-native download
            const a = document.createElement('a');
            a.href = DOWNLOAD_URL;
            a.download = FILE_NAME;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setProgress(100);
            setDlState('downloaded');
        }
    }

    const handleConnect = () => {
        if (!userMe?.id) return;
        setAutoState('connecting');
        connectElectronApp(userMe.id);
        setTimeout(() => setAutoState('connected'), 1500);
        setTimeout(onClose, 3000);
    };

    const canGoNext = dlState === 'downloaded' && installed;

    const nextTooltip = dlState !== 'downloaded'
        ? 'Download the app first'
        : !installed
            ? 'Confirm installation to continue'
            : '';

    // While downloading — show only floating mini-widget, no modal overlay
    if (dlState === 'downloading') {
        return <FloatingDownload fileName={FILE_NAME} progress={progress} />;
    }

    // Auto-connect states — shown instead of the full wizard
    if (autoState === 'probing' || autoState === 'connecting' || autoState === 'connected') {
        const isConnected = autoState === 'connected';
        return (
            <div className="backwrap" style={{ zIndex: 9999 }}>
                <div style={{
                    background: '#fff', borderRadius: 16, width: 380,
                    maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    padding: '36px 32px', textAlign: 'center',
                }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: '50%', margin: '0 auto 18px',
                        background: isConnected ? '#eafaf1' : 'rgba(49,143,255,0.10)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 26,
                    }}>
                        {isConnected ? '✓' : '⟳'}
                    </div>
                    <p style={{ fontWeight: 700, fontSize: 16, color: '#0b1f47', margin: '0 0 8px' }}>
                        {isConnected ? 'Connected!' : autoState === 'probing' ? 'Detecting app…' : 'Connecting…'}
                    </p>
                    <p style={{ color: '#636e82', fontSize: 13, margin: 0 }}>
                        {isConnected
                            ? 'Freeli Call is linked to your account.'
                            : 'Freeli Call app detected — setting up your account automatically.'}
                    </p>
                    {isConnected && (
                        <button onClick={onClose} style={{
                            marginTop: 20, background: '#318fff', color: '#fff',
                            border: 'none', borderRadius: 20, padding: '8px 24px',
                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}>Done</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="backwrap" style={{ zIndex: 9999 }}>
            <div style={{
                background: '#fff',
                borderRadius: 16,
                width: 480,
                maxWidth: '95vw',
                boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    background: '#0b1f47',
                    padding: '18px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>
                        Setup freeli call app
                    </span>
                    <button
                        onClick={onClose}
                        title="Close"
                        style={{
                            background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%',
                            width: 28, height: 28, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', color: '#fff', fontSize: 18, lineHeight: 1,
                            cursor: 'pointer', flexShrink: 0,
                        }}
                    >×</button>
                </div>

                {/* Step progress */}
                <div style={{
                    display: 'flex', alignItems: 'center',
                    padding: '16px 24px', borderBottom: '1px solid #e4e8ee',
                    background: '#f8f9fb',
                }}>
                    <StepDot index={0} current={step} />
                    <Connector done={step > 0} />
                    <StepDot index={1} current={step} />
                </div>

                {/* Step content */}
                <div style={{ padding: '28px 28px 20px' }}>

                    {/* ── Step 1 — Download & Install ── */}
                    {step === 0 && (
                        <>
                            <p style={{ fontWeight: 700, fontSize: 16, color: '#0b1f47', margin: '0 0 6px' }}>
                                Download &amp; install freeli call
                            </p>
                            <p style={{ color: '#636e82', fontSize: 13, margin: '0 0 20px' }}>
                                Already have it installed and running?{' '}
                                <span
                                    onClick={() => setStep(1)}
                                    style={{ color: '#318fff', cursor: 'pointer', fontWeight: 600 }}
                                >
                                    skip to connect →
                                </span>
                            </p>

                            {/* Idle — show download button */}
                            {dlState === 'idle' && (
                                <button
                                    onClick={handleDownload}
                                    style={{
                                        background: '#318fff', color: '#fff', border: 'none',
                                        borderRadius: 20, padding: '10px 24px', fontSize: 13,
                                        fontWeight: 600, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 8,
                                    }}
                                >
                                    ⬇ Download {FILE_NAME}
                                </button>
                            )}

                            {/* Downloaded — simplified */}
                            {dlState === 'downloaded' && (
                                <>
                                    {/* Single success line */}
                                    <div style={{
                                        background: '#eafaf1', border: '1px solid #b2e8cc',
                                        borderRadius: 10, padding: '12px 16px', marginBottom: 14,
                                        display: 'flex', alignItems: 'center', gap: 10,
                                    }}>
                                        <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>✅</span>
                                        <p style={{ margin: 0, fontSize: 13, color: '#1a7a44' }}>
                                            {isLinux
                                                ? <>Download complete! Open a terminal and run the command below to install.</>
                                                : <>Download complete! Check your <strong>Downloads folder</strong> or click the file in your browser's download bar to run the installer.</>
                                            }
                                        </p>
                                    </div>

                                    {/* Linux install command */}
                                    {isLinux && (
                                        <div style={{
                                            background: '#0b1f47', borderRadius: 10,
                                            padding: '14px 18px', marginBottom: 14,
                                        }}>
                                            <div style={{ fontSize: 11, color: '#8fa3c8', marginBottom: 8, letterSpacing: 0.4 }}>
                                                TERMINAL — run in your Downloads folder
                                            </div>
                                            <code style={{
                                                display: 'block', fontSize: 13, color: '#a3e635',
                                                letterSpacing: 0.3, userSelect: 'all',
                                                wordBreak: 'break-all',
                                            }}>
                                                sudo dpkg -i freeli-call.deb
                                            </code>
                                            <div style={{ fontSize: 11, color: '#8fa3c8', marginTop: 8 }}>
                                                If dpkg reports missing dependencies, run:{' '}
                                                <code style={{ color: '#93c5fd' }}>sudo apt-get install -f</code>
                                            </div>
                                        </div>
                                    )}


                                    {/* Install confirmation checkbox */}
                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        cursor: 'pointer', padding: '12px 16px',
                                        background: installed ? '#eafaf1' : '#f8f9fb',
                                        border: `1px solid ${installed ? '#b2e8cc' : '#d8dbe0'}`,
                                        borderRadius: 10, fontSize: 13,
                                        color: installed ? '#1a7a44' : '#0b1f47',
                                        fontWeight: installed ? 600 : 400,
                                        transition: 'background 0.2s, border-color 0.2s',
                                        userSelect: 'none',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={installed}
                                            onChange={e => setInstalled(e.target.checked)}
                                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#318fff', flexShrink: 0 }}
                                        />
                                        {installed
                                            ? '✓ Freeli Call is installed and running in my system tray'
                                            : "I've installed Freeli Call and it's running in my system tray"}
                                    </label>
                                </>
                            )}
                        </>
                    )}

                    {/* ── Step 2 — Connect ── */}
                    {step === 1 && (
                        <>
                            <p style={{ fontWeight: 700, fontSize: 16, color: '#0b1f47', margin: '0 0 6px' }}>
                                Connect your account
                            </p>
                            <p style={{ color: '#636e82', fontSize: 13, margin: '0 0 20px' }}>
                                Link your freeli account so the desktop app can receive your calls.
                            </p>
                            <div style={{
                                background: '#f8f9fb', border: '1px solid #d8dbe0',
                                borderRadius: 10, padding: '16px 20px', marginBottom: 16,
                            }}>
                                <p style={{ margin: '0 0 8px', fontSize: 13, color: '#0b1f47' }}>
                                    Click <strong>Connect account</strong> — your credentials will be sent
                                    to the running Freeli Call app automatically.
                                </p>
                                <p style={{ margin: 0, fontSize: 12, color: '#636e82' }}>
                                    This lets the app listen for incoming calls even when the browser is closed.
                                </p>
                            </div>
                            <button
                                onClick={handleConnect}
                                style={{
                                    background: '#318fff',
                                    color: '#fff', border: 'none', borderRadius: 20,
                                    padding: '9px 24px', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Connect account
                            </button>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 28px 20px', borderTop: '1px solid #e4e8ee',
                }}>
                    <button
                        onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
                        style={{
                            background: 'none', border: '1px solid #d8dbe0',
                            borderRadius: 20, padding: '7px 18px',
                            fontSize: 13, cursor: 'pointer', color: '#636e82',
                        }}
                    >
                        {step === 0 ? 'Cancel' : '← Back'}
                    </button>

                    {step === 0 ? (
                        <button
                            onClick={() => canGoNext && setStep(1)}
                            disabled={!canGoNext}
                            title={nextTooltip}
                            style={{
                                background: canGoNext ? '#318fff' : '#e4e8ee',
                                color: canGoNext ? '#fff' : '#a0a8b5',
                                border: 'none', borderRadius: 20,
                                padding: '7px 22px', fontSize: 13, fontWeight: 600,
                                cursor: canGoNext ? 'pointer' : 'not-allowed',
                                transition: 'background 0.2s, color 0.2s',
                            }}
                        >
                            Next →
                        </button>
                    ) : (
                        <button
                            onClick={onClose}
                            style={{
                                background: '#318fff',
                                color: '#fff',
                                border: 'none', borderRadius: 20,
                                padding: '7px 22px', fontSize: 13, fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Finish ✓
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
